import React from 'react';
import { ComingSoon } from '../coming-soon';
import * as S from './detail-panel.styles';

/**
 * Composed pieces for an entity detail panel. Layout lives in `detail-panel.styles`.
 *
 * The one opinion encoded here is how an ABSENT value renders, and it is the reason this is a
 * component rather than markup repeated at each call site.
 */

export interface IDetailSectionProps {
    /** Uppercase section kicker — `IDENTIFIERS`. */
    title: string;
    /** Optional right-aligned note, e.g. why a section is empty. */
    meta?: React.ReactNode;
    children: React.ReactNode;
}

export function DetailSection({ title, meta, children }: IDetailSectionProps): React.ReactElement {
    return (
        <S.Section>
            <S.SectionHead>
                <S.SectionTitle>{title}</S.SectionTitle>
                {meta && <S.SectionMeta>{meta}</S.SectionMeta>}
            </S.SectionHead>
            {children}
        </S.Section>
    );
}

export interface IDetailFieldProps {
    label: string;
    /** The value. Undefined, null or empty renders as `absent` — never as a blank line. */
    value?: React.ReactNode;
    /**
     * What to say when there is no value.
     *
     * Defaults to "Not recorded", which describes OUR record and claims nothing about the
     * world. On a claims console the distinction is load-bearing: a bare `—` invites reading
     * an absent taxonomy code as "this provider has no taxonomy", which is a statement about
     * their enrolment rather than about what we store (NS-3).
     */
    absent?: string;
    /**
     * Set when the API exposes NO field for this at all, with a hint explaining that.
     *
     * Distinct from `absent`, and the distinction is the whole point on a claims console:
     *
     * - **Not recorded** — the field exists and this provider has no value in it. A fact
     *   about the record.
     * - **Not available** — we hold no such field. A fact about what we can know.
     *
     * Collapsing the two renders a DEA registration we cannot store as one this provider
     * does not have, which is a statement about their prescribing authority (NS-4).
     */
    unavailable?: string;
}

const isEmpty = (value: React.ReactNode): boolean =>
    value === undefined || value === null || value === '' || (typeof value === 'string' && !value.trim());

export function DetailField({ label, value, absent = 'Not recorded', unavailable }: IDetailFieldProps): React.ReactElement {
    // "We hold no field for this" outranks "this record has no value", because it is true
    // regardless of what the record contains — there is nothing to have been filled in.
    if (unavailable) {
        return (
            <S.Field>
                <S.FieldLabel>{label}</S.FieldLabel>
                <ComingSoon hint={unavailable}>
                    <S.FieldAbsent>Not available</S.FieldAbsent>
                </ComingSoon>
            </S.Field>
        );
    }

    return (
        <S.Field>
            <S.FieldLabel>{label}</S.FieldLabel>
            {isEmpty(value) ? <S.FieldAbsent>{absent}</S.FieldAbsent> : <S.FieldValue>{value}</S.FieldValue>}
        </S.Field>
    );
}

/**
 * A whole section we cannot back yet, said ONCE.
 *
 * Preferred over a stack of `DetailField … unavailable` rows whenever every field in a section
 * is missing for the same reason. Three rows each reading "Not available" implies three
 * separate lookups that each came back empty, which is both wrong and — measured on the
 * credentialing section — visually dominant enough that the dialog reads as broken rather than
 * as scoped. One note states one gap, and says why.
 *
 * Use the per-field `unavailable` form instead when a section is only PARTLY unbacked: a lone
 * absent row among populated ones reads correctly and keeps the field visible.
 */
export function DetailUnavailable({ children }: { children: React.ReactNode }): React.ReactElement {
    return <S.Empty>{children}</S.Empty>;
}

